import { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '@/components/feature/Layout';
import { supabase, Producto, Kardex } from '@/lib/supabase';
import {
  PeriodoCPP,
  EstadoCPP,
  calcularRango,
  construirEstadoCPP,
  formatearBs,
} from '@/lib/cpp';
import ResumenCards from '@/pages/valuacion-cpp/components/ResumenCards';
import TablaCPP from '@/pages/valuacion-cpp/components/TablaCPP';

const PERIODOS: { key: PeriodoCPP; label: string }[] = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'semana', label: 'Semana' },
  { key: 'mes', label: 'Mes' },
  { key: 'anio', label: 'Año' },
];

const hoyISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function ValuacionCppPage() {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loadingProductos, setLoadingProductos] = useState(true);
  const [selectedProducto, setSelectedProducto] = useState<Producto | null>(null);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [periodo, setPeriodo] = useState<PeriodoCPP>('mes');
  const [refDate, setRefDate] = useState<string>(hoyISO());
  const [estado, setEstado] = useState<EstadoCPP | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        const { data } = await supabase.from('productos').select('*').order('nombre');
        if (data) setProductos(data as Producto[]);
      } catch {
        showToast('No se pudieron cargar los productos', 'error');
      } finally {
        setLoadingProductos(false);
      }
    };
    fetchProductos();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const rango = useMemo(
    () => calcularRango(periodo, new Date(`${refDate}T00:00:00`)),
    [periodo, refDate]
  );

  const cargarValuacion = async () => {
    if (!selectedProducto) return;
    setLoadingData(true);
    try {
      const { data, error } = await supabase
        .from('kardex')
        .select('*')
        .eq('producto_id', selectedProducto.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setEstado(construirEstadoCPP((data as Kardex[]) || [], rango.desde, rango.hasta));
    } catch {
      showToast('No se pudo cargar la valuación. Inténtalo nuevamente.', 'error');
      setEstado(null);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    cargarValuacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducto, periodo, refDate]);

  const resultados = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = [...productos];
    if (!q) return base.slice(0, 8);
    return base
      .filter(
        (p) =>
          p.nombre.toLowerCase().includes(q) ||
          (p.marca && p.marca.toLowerCase().includes(q)) ||
          (p.codigo_interno && p.codigo_interno.toLowerCase().includes(q))
      )
      .slice(0, 10);
  }, [productos, search]);

  const seleccionarProducto = (p: Producto) => {
    setSelectedProducto(p);
    setSearch('');
    setShowDropdown(false);
  };

  return (
    <Layout>
      <div className="fade-in space-y-6">
        {toast && (
          <div
            className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl text-sm font-medium animate-slide-in ${
              toast.type === 'success' ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'
            }`}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">Valuación de inventario CPP</h2>
            <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
              Costo Promedio Ponderado (CPP / PMP). El costo unitario se calcula dividiendo el valor total
              del inventario disponible entre las unidades disponibles.
            </p>
          </div>
          <button
            onClick={cargarValuacion}
            disabled={!selectedProducto || loadingData}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#2563EB] text-white rounded-lg text-sm font-medium hover:bg-[#1E40AF] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className={`ri-refresh-line w-4 h-4 flex items-center justify-center ${loadingData ? 'animate-spin' : ''}`}></i>
            Actualizar
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Selector de producto */}
            <div className="lg:col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Producto <span className="text-[#DC2626]">*</span>
              </label>
              <div ref={dropdownRef} className="relative">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                  <input
                    type="text"
                    value={selectedProducto && !showDropdown
                      ? `${selectedProducto.nombre}`
                      : search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setSelectedProducto(null);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Buscar por nombre, código interno o marca..."
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                  />
                </div>
                {showDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg max-h-64 overflow-y-auto z-20">
                    {loadingProductos ? (
                      <div className="px-4 py-4 text-sm text-gray-400">Cargando productos...</div>
                    ) : resultados.length === 0 ? (
                      <div className="px-4 py-4 text-sm text-gray-400">No se encontraron productos</div>
                    ) : (
                      resultados.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => seleccionarProducto(p)}
                          className="w-full text-left px-4 py-2.5 hover:bg-[#F4F6F8] transition-colors cursor-pointer"
                        >
                          <p className="text-sm font-medium text-[#111827] truncate">{p.nombre}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {p.marca || 'Sin marca'}
                            {p.codigo_interno ? ` · ${p.codigo_interno}` : ''} — Stock: {p.stock}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Período */}
            <div className="lg:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Período</label>
              <div className="flex items-center bg-[#F4F6F8] rounded-lg p-1 gap-1">
                {PERIODOS.map((op) => (
                  <button
                    key={op.key}
                    onClick={() => setPeriodo(op.key)}
                    className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      periodo === op.key ? 'bg-white text-[#111827]' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {op.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Fecha de referencia */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha de referencia</label>
              <input
                type="date"
                value={refDate}
                onChange={(e) => setRefDate(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#2563EB] cursor-pointer"
              />
            </div>
          </div>

          {selectedProducto && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-2 text-gray-500">
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                Período: <strong className="text-[#111827] capitalize">{rango.label}</strong>
              </span>
              <span className="inline-flex items-center gap-2 text-gray-500">
                <i className="ri-archive-line w-4 h-4 flex items-center justify-center"></i>
                Stock actual: <strong className="text-[#111827]">{selectedProducto.stock} u.</strong>
              </span>
              <span className="inline-flex items-center gap-2 text-gray-500">
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                CPP actual: <strong className="text-[#111827]">{formatearBs(Number(selectedProducto.costo_promedio) || 0)}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Contenido */}
        {!selectedProducto ? (
          <div className="bg-white rounded-xl border border-gray-100 py-16 text-center">
            <div className="w-16 h-16 bg-[#2563EB]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-scales-3-line text-[#2563EB] text-2xl w-7 h-7 flex items-center justify-center"></i>
            </div>
            <p className="text-sm font-medium text-[#111827]">Selecciona un producto y un período</p>
            <p className="text-xs text-gray-400 mt-1">La tabla de valuación CPP se mostrará aquí cuando elijas un producto.</p>
          </div>
        ) : loadingData ? (
          <div className="bg-white rounded-xl border border-gray-100 py-16">
            <div className="text-center">
              <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
              <p className="text-sm text-gray-500">Calculando valuación CPP...</p>
            </div>
          </div>
        ) : estado ? (
          <div className="space-y-6">
            <ResumenCards estado={estado} />

            {!estado.tieneMovimientos && (
              <div className="bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-xl px-4 py-3 flex items-start gap-3">
                <i className="ri-information-line text-[#F59E0B] w-5 h-5 flex items-center justify-center mt-0.5"></i>
                <p className="text-sm text-gray-600">
                  No se encontraron movimientos para este producto en el período seleccionado. Los saldos mostrados
                  corresponden al inicio del período.
                </p>
              </div>
            )}

            <TablaCPP filas={estado.filas} />
          </div>
        ) : null}
      </div>
    </Layout>
  );
}